import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, Truck, Users, ArrowLeft, Lock } from "lucide-react";
import { ImageUpload } from "../components/ImageUpload";
import {
  getCourierSettingsPublic, quoteParcel, parcelDistance, createParcel, myParcels,
  listParcelOffers, acceptParcelOffer, payParcel, confirmParcelDelivery,
} from "../lib/api";

const GREEN = "#075A43";
const ORANGE = "#FF5A1F";
const field = { background: "#f5f7f6", border: "1px solid #e5e7eb" };
const inp = "w-full rounded-xl px-4 h-11 text-sm outline-none";

export default function ParcelService() {
  const nav = useNavigate();
  const [tab, setTab] = useState("send");
  const [settings, setSettings] = useState(null);
  const [mine, setMine] = useState([]);
  const [offers, setOffers] = useState({});
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [distBusy, setDistBusy] = useState(false);
  const [distLabel, setDistLabel] = useState(null);
  const [f, setF] = useState({
    mode: "labase", service: "express", pickup_address: "", pickup_zone: "", dropoff_address: "",
    dropoff_zone: "", sender_name: "", sender_phone: "", weight_kg: "", size_label: "",
    vehicle: "", distance_km: "", photo_url: "", proposed_price_chf: "", notes: "",
  });
  const set = (p) => setF((s) => ({ ...s, ...p }));

  const loadMine = () => myParcels().then(setMine).catch(() => {});
  useEffect(() => { getCourierSettingsPublic().then(setSettings).catch(() => {}); loadMine(); }, []);

  const calcDistance = async () => {
    if (!f.pickup_address || !f.dropoff_address) { toast.error("Renseignez les 2 adresses"); return; }
    setDistBusy(true);
    try {
      const r = await parcelDistance({
        pickup_address: f.pickup_address, dropoff_address: f.dropoff_address,
        pickup_zone: f.pickup_zone || null, dropoff_zone: f.dropoff_zone || null, vehicle: f.vehicle || null,
      });
      set({ distance_km: String(r.distance_km) });
      setDistLabel(`≈ ${r.distance_km} km (${r.duration_min} min) · ${r.pickup_label} → ${r.dropoff_label}`);
      if (f.mode === "labase") {
        const q = await quoteParcel({
          distance_km: r.distance_km, weight_kg: parseFloat(f.weight_kg) || 0,
          size_label: f.size_label || null, vehicle: f.vehicle, service: f.service,
        });
        setQuote(q);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Distance indisponible — saisie manuelle"); }
    finally { setDistBusy(false); }
  };

  const doQuote = async () => {
    try {
      const q = await quoteParcel({
        distance_km: parseFloat(f.distance_km) || 0, weight_kg: parseFloat(f.weight_kg) || 0,
        size_label: f.size_label || null, vehicle: f.vehicle, service: f.service,
      });
      setQuote(q);
    } catch { toast.error("Devis impossible"); }
  };

  const submit = async () => {
    if (!f.pickup_address || !f.dropoff_address || !f.sender_name || !f.sender_phone || !f.pickup_zone || !f.dropoff_zone) {
      toast.error("Renseignez adresses, zones et vos coordonnées"); return;
    }
    if (f.mode === "c2c" && !f.proposed_price_chf) { toast.error("Indiquez un prix proposé"); return; }
    setBusy(true);
    try {
      await createParcel({
        mode: f.mode, pickup_address: f.pickup_address, sender_name: f.sender_name, sender_phone: f.sender_phone,
        pickup_zone: f.pickup_zone, dropoff_address: f.dropoff_address, dropoff_zone: f.dropoff_zone,
        weight_kg: parseFloat(f.weight_kg) || 0, size_label: f.size_label || null, photo_url: f.photo_url || null,
        vehicle: f.vehicle, service: f.service, distance_km: parseFloat(f.distance_km) || 0,
        proposed_price_chf: f.mode === "c2c" ? parseFloat(f.proposed_price_chf) : null, notes: f.notes || null,
      });
      toast.success("Demande créée"); setQuote(null); setTab("mine"); loadMine();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); } finally { setBusy(false); }
  };

  const loadOffers = async (id) => {
    try { const o = await listParcelOffers(id); setOffers((s) => ({ ...s, [id]: o })); } catch { /* ignore */ }
  };
  const accept = async (id, offerId) => {
    setBusy(true);
    try { await acceptParcelOffer(id, offerId); toast.success("Offre acceptée"); loadMine(); } catch { toast.error("Échec"); } finally { setBusy(false); }
  };
  const pay = async (id) => {
    setBusy(true);
    try { await payParcel(id); toast.success("Paiement effectué (simulation). Identité révélée au livreur."); loadMine(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec paiement"); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="parcel-page">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm font-bold mb-4" style={{ color: GREEN }}><ArrowLeft size={18} /> Retour</button>
      <div className="flex items-center gap-2 mb-1"><Package size={24} color={GREEN} /><h1 className="text-2xl font-extrabold" style={{ color: GREEN }}>Livraison colis & courrier</h1></div>
      <p className="text-sm mb-4" style={{ color: "#64748b" }}>Express ou programmé. Vos coordonnées restent masquées jusqu’au paiement.</p>

      <div className="flex gap-2 mb-5">
        <button data-testid="parcel-tab-send" onClick={() => setTab("send")} className="rounded-full px-4 h-9 text-sm font-bold" style={tab === "send" ? { background: GREEN, color: "#fff" } : field}>Envoyer</button>
        <button data-testid="parcel-tab-mine" onClick={() => setTab("mine")} className="rounded-full px-4 h-9 text-sm font-bold" style={tab === "mine" ? { background: GREEN, color: "#fff" } : field}>Mes envois</button>
        <button data-testid="parcel-courier-link" onClick={() => nav("/livreur")} className="rounded-full px-4 h-9 text-sm font-bold ml-auto" style={field}>Devenir livreur</button>
      </div>

      {tab === "send" ? (
        <div className="space-y-3" data-testid="parcel-form">
          <div className="flex gap-2">
            <button data-testid="parcel-mode-labase" onClick={() => set({ mode: "labase" })} className="flex-1 rounded-xl p-3 text-left" style={{ border: `2px solid ${f.mode === "labase" ? GREEN : "#e5e7eb"}` }}>
              <Truck size={18} color={GREEN} /><p className="font-bold text-sm mt-1" style={{ color: GREEN }}>Livreurs La BASE</p><p className="text-xs" style={{ color: "#64748b" }}>Tarif au km (Fondateur)</p>
            </button>
            <button data-testid="parcel-mode-c2c" onClick={() => set({ mode: "c2c" })} className="flex-1 rounded-xl p-3 text-left" style={{ border: `2px solid ${f.mode === "c2c" ? GREEN : "#e5e7eb"}` }}>
              <Users size={18} color={GREEN} /><p className="font-bold text-sm mt-1" style={{ color: GREEN }}>Entre particuliers</p><p className="text-xs" style={{ color: "#64748b" }}>Vous proposez un prix</p>
            </button>
          </div>
          <div className="flex gap-2">
            {["express", "scheduled"].map((sv) => (
              <button key={sv} data-testid={`parcel-service-${sv}`} onClick={() => set({ service: sv })} className="flex-1 rounded-xl h-10 text-sm font-bold" style={f.service === sv ? { background: ORANGE, color: "#fff" } : field}>{sv === "express" ? "Express" : "Programmé"}</button>
            ))}
          </div>

          <p className="text-xs font-bold flex items-center gap-1" style={{ color: "#64748b" }}><Lock size={12} /> Adresses VISIBLES par les livreurs (trajet). Votre nom + téléphone restent masqués jusqu’au paiement.</p>
          <input data-testid="parcel-pickup" value={f.pickup_address} onChange={(e) => set({ pickup_address: e.target.value })} placeholder="Adresse d'enlèvement exacte" className={inp} style={field} />
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="parcel-pickup-zone" value={f.pickup_zone} onChange={(e) => set({ pickup_zone: e.target.value })} placeholder="Zone publique (ex : 1470 Estavayer)" className={inp} style={field} />
            <input data-testid="parcel-dropoff-zone" value={f.dropoff_zone} onChange={(e) => set({ dropoff_zone: e.target.value })} placeholder="Zone livraison (ex : 1530 Payerne)" className={inp} style={field} />
          </div>
          <input data-testid="parcel-dropoff" value={f.dropoff_address} onChange={(e) => set({ dropoff_address: e.target.value })} placeholder="Adresse de livraison" className={inp} style={field} />
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="parcel-sender-name" value={f.sender_name} onChange={(e) => set({ sender_name: e.target.value })} placeholder="Votre nom (masqué)" className={inp} style={field} />
            <input data-testid="parcel-sender-phone" value={f.sender_phone} onChange={(e) => set({ sender_phone: e.target.value })} placeholder="Votre téléphone (masqué)" className={inp} style={field} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <input data-testid="parcel-weight" value={f.weight_kg} onChange={(e) => set({ weight_kg: e.target.value })} inputMode="decimal" placeholder="Poids (kg)" className={inp} style={field} />
            <select data-testid="parcel-size" value={f.size_label} onChange={(e) => set({ size_label: e.target.value })} className={inp} style={field}>
              <option value="">Taille…</option>
              {(settings?.size_tiers || []).map((t) => <option key={t.label} value={t.label}>{t.label}</option>)}
            </select>
            <select data-testid="parcel-vehicle" value={f.vehicle} onChange={(e) => set({ vehicle: e.target.value })} className={inp} style={field}>
              <option value="">Véhicule souhaité (facultatif)</option>
              {(settings?.vehicles || []).map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <button type="button" data-testid="parcel-calc-distance" onClick={calcDistance} disabled={distBusy} className="rounded-xl font-bold px-3 h-11 text-white whitespace-nowrap" style={{ background: GREEN, opacity: distBusy ? 0.6 : 1 }}>📍 {distBusy ? "Calcul…" : "Calculer la distance"}</button>
              <input data-testid="parcel-distance" value={f.distance_km} onChange={(e) => set({ distance_km: e.target.value })} inputMode="decimal" placeholder="Distance (km)" className={inp} style={field} />
            </div>
            {distLabel && <p data-testid="parcel-distance-resolved" className="text-xs" style={{ color: "#64748b" }}>{distLabel}</p>}
          </div>
          <div><ImageUpload testid="parcel-photo" value={f.photo_url} onChange={(url) => set({ photo_url: url })} /></div>

          {f.mode === "c2c" ? (
            <input data-testid="parcel-proposed-price" value={f.proposed_price_chf} onChange={(e) => set({ proposed_price_chf: e.target.value })} inputMode="decimal" placeholder="Prix proposé (CHF)" className={inp} style={field} />
          ) : (
            <div className="flex items-center gap-2">
              <button data-testid="parcel-quote-btn" onClick={doQuote} className="rounded-xl font-bold px-4 h-11 text-white" style={{ background: ORANGE }}>Estimer le prix</button>
              {quote && <span data-testid="parcel-quote-result" className="font-extrabold" style={{ color: GREEN }}>≈ CHF {Number(quote.price_chf).toFixed(2)}</span>}
            </div>
          )}
          <button data-testid="parcel-submit" onClick={submit} disabled={busy} className="w-full rounded-xl font-bold text-white h-12" style={{ background: GREEN, opacity: busy ? 0.6 : 1 }}>Créer la demande</button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="parcel-mine-list">
          {mine.length === 0 && <p className="text-center" style={{ color: "#64748b" }}>Aucun envoi pour l’instant.</p>}
          {mine.map((p) => (
            <div key={p.id} data-testid={`parcel-card-${p.id}`} className="rounded-2xl p-4" style={{ border: "1px solid #f0f0f0" }}>
              <div className="flex items-center justify-between">
                <p className="font-bold" style={{ color: GREEN }}>{p.pickup_zone} → {p.dropoff_zone}</p>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "#f1f5f9", color: "#64748b" }}>{p.mode === "c2c" ? "Particuliers" : "La BASE"} · {p.status}</span>
              </div>
              <p className="text-sm mt-1" style={{ color: "#1f2937" }}>Prix : <strong>CHF {Number(p.price_chf || 0).toFixed(2)}</strong>{p.commission_chf != null && <span style={{ color: "#64748b" }}> (commission {p.commission_chf} / livreur {p.courier_payout_chf})</span>}</p>
              {p.mode === "c2c" && p.status === "open" && (
                <div className="mt-2">
                  <button data-testid={`parcel-offers-btn-${p.id}`} onClick={() => loadOffers(p.id)} className="text-xs font-bold px-3 h-8 rounded-lg" style={field}>Voir les offres</button>
                  {(offers[p.id] || []).map((o) => (
                    <div key={o.id} className="flex items-center justify-between mt-2 text-sm">
                      <span>Offre : <strong>CHF {o.price_chf}</strong> ({o.kind === "accept" ? "accepte votre prix" : "contre-proposition"})</span>
                      <button data-testid={`parcel-accept-${o.id}`} onClick={() => accept(p.id, o.id)} className="text-xs font-bold px-3 h-8 rounded-lg text-white" style={{ background: GREEN }}>Accepter</button>
                    </div>
                  ))}
                </div>
              )}
              {p.payment_status !== "paid" && (p.status === "quoted" || p.status === "accepted") && (
                <button data-testid={`parcel-pay-${p.id}`} onClick={() => pay(p.id)} disabled={busy} className="mt-2 w-full rounded-xl font-bold text-white h-10" style={{ background: ORANGE }}>Payer CHF {Number(p.price_chf || 0).toFixed(2)}</button>
              )}
              {p.payment_status === "paid" && <p data-testid={`parcel-paid-${p.id}`} className="mt-2 text-sm font-bold" style={{ color: GREEN }}>✓ Payé (séquestre) — le livreur a reçu votre contact</p>}
              {p.payment_status === "paid" && p.escrow_status === "held" && (
                <button data-testid={`parcel-confirm-${p.id}`} onClick={async () => { setBusy(true); try { await confirmParcelDelivery(p.id); toast.success("Livraison confirmée — paiement libéré au livreur"); loadMine(); } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); } finally { setBusy(false); } }} disabled={busy} className="mt-2 w-full rounded-xl font-bold h-10" style={{ background: "#eafff6", color: GREEN, border: `1px solid ${GREEN}` }}>Confirmer la livraison (libère le paiement)</button>
              )}
              {p.escrow_status === "released" && <p className="mt-1 text-xs font-bold" style={{ color: GREEN }}>✓ Livré — paiement libéré</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
